import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { AnimatePresence, m } from 'motion/react';
import { Check, ChevronDown } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';

const EASE = [0.16, 1, 0.3, 1] as const;

export interface SelectOption<T> {
  value: T;
  label: string;
  icon?: LucideIcon;
  color?: string;
}

interface SelectProps<T> {
  value: T;
  options: SelectOption<T>[];
  onChange: (value: T) => void;
  placeholder?: string;
  id?: string;
  'aria-label'?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * Dropdown estilizado (listbox acessível por teclado). Fecha ao clicar fora / Escape.
 * <Select<T>> genérico: value + options {value,label,icon?,color?}.
 */
export function Select<T extends string | number>({
  value,
  options,
  onChange,
  placeholder = 'Selecione',
  id,
  'aria-label': ariaLabel,
  className,
  disabled = false,
}: SelectProps<T>) {
  const autoId = useId();
  const listId = `${id ?? autoId}-list`;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  const selectedIndex = options.findIndex((o) => o.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  useEffect(() => {
    if (!open) return;
    setActive(selectedIndex >= 0 ? selectedIndex : 0);
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, selectedIndex]);

  const pick = (index: number) => {
    const opt = options[index];
    if (!opt) return;
    onChange(opt.value);
    setOpen(false);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        setOpen(false);
        break;
      case 'ArrowDown':
        e.preventDefault();
        setActive((i) => Math.min(i + 1, options.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActive((i) => Math.max(i - 1, 0));
        break;
      case 'Home':
        e.preventDefault();
        setActive(0);
        break;
      case 'End':
        e.preventDefault();
        setActive(options.length - 1);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        pick(active);
        break;
    }
  };

  const SelIcon = selected?.icon;

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <button
        type="button"
        id={id}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        onKeyDown={onKeyDown}
        className={cn(
          'flex h-11 w-full items-center gap-2 rounded-lg border border-field bg-surface px-3 text-left text-[15px] transition-colors',
          'hover:border-muted focus-visible:border-primary focus-visible:outline-none',
        )}
      >
        {SelIcon && (
          <SelIcon className="size-4 shrink-0" style={selected?.color ? { color: selected.color } : undefined} />
        )}
        <span className={cn('flex-1 truncate', selected ? 'text-ink' : 'text-muted')}>
          {selected?.label ?? placeholder}
        </span>
        <ChevronDown className={cn('size-4 shrink-0 text-muted transition-transform', open && 'rotate-180')} />
      </button>

      <AnimatePresence>
        {open && (
          <m.ul
            id={listId}
            role="listbox"
            aria-activedescendant={`${listId}-opt-${active}`}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15, ease: EASE }}
            className="absolute z-40 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-border bg-surface p-1 shadow-hover"
          >
            {options.map((opt, i) => {
              const OptIcon = opt.icon;
              const isSelected = opt.value === value;
              const isActive = i === active;
              return (
                <li
                  key={String(opt.value)}
                  id={`${listId}-opt-${i}`}
                  role="option"
                  aria-selected={isSelected}
                  onPointerEnter={() => setActive(i)}
                  onClick={() => pick(i)}
                  className={cn(
                    'flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-[15px] text-ink',
                    isActive && 'bg-canvas',
                  )}
                >
                  {OptIcon && (
                    <OptIcon className="size-4 shrink-0" style={opt.color ? { color: opt.color } : undefined} />
                  )}
                  <span className="flex-1 truncate">{opt.label}</span>
                  {isSelected && <Check className="size-4 shrink-0 text-primary" />}
                </li>
              );
            })}
          </m.ul>
        )}
      </AnimatePresence>
    </div>
  );
}
